import CameraFrontIcon from '@mui/icons-material/CameraFront'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import {
  Alert,
  Avatar,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  CircularProgress,
  Grid,
  IconButton,
  Snackbar,
  TextFieldProps,
  Typography,
} from '@mui/material'
import { grey, red } from '@mui/material/colors'
import * as Sentry from '@sentry/react'
import {
  ErrorMessage,
  FormikFormProps,
  FormikValues,
  useField,
  useFormikContext,
} from 'formik'
import React, { InputHTMLAttributes, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { checkFileExists, putToS3 } from '../../../hooks/useS3Storage'
import { SnackBarAlertType } from '../../types/interface'

interface ProfileUploadWrapperProps
  extends InputHTMLAttributes<HTMLInputElement> {
  folder: string
  name: string
}

interface ProgressType {
  isLoading: boolean
  success: boolean
}

const ProfileUploadWrapper: React.FC<
  ProfileUploadWrapperProps & TextFieldProps
> = ({ folder, name, ...otherProps }) => {
  const [field, meta] = useField(name)
  const inputRef = useRef<HTMLInputElement>(null)
  const { setFieldError, setFieldTouched, setFieldValue, setSubmitting } =
    useFormikContext<FormikFormProps & FormikValues>()
  const [progress, setProgress] = useState<ProgressType>({
    isLoading: false,
    success: false,
  })
  const [preview, setPreview] = useState<ArrayBuffer | null | string>(null)
  const [alertEvent, setAlertEvent] = useState<SnackBarAlertType>({
    message: '',
    open: false,
    type: 'info',
  })
  const { t } = useTranslation()

  const previewImage = ({ file }: { file: File }) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => {
      setPreview(reader.result)
    }
  }

  const isValid = (file: File): boolean => {
    if (!file) {
      setFieldError(name, 'No file selected')
      return false
    }
    // Check if file type and size is valid
    const SUPPORTED_FORMATS = ['image/jpg', 'image/jpeg', 'image/png']
    const MAX_FILE_SIZE = 5242880 // 5MB

    if (SUPPORTED_FORMATS.includes(file.type)) {
      if (file.size <= MAX_FILE_SIZE) {
        return true
      }
      setFieldError(name, 'File size is too large')
      return false
    }
    setFieldError(name, 'File type is not supported')
    return false
  }

  const handleUpload = async (file: File) => {
    try {
      setSubmitting(true)
      setProgress({
        ...progress,
        isLoading: true,
      })

      const blobData = await file.arrayBuffer()
      const blob = new Blob([blobData], { type: file.type })

      const fileToUpload = new File(
        [blob],
        file.name ||
          `profile-${Date.now()}.${file.type.split('/')[1] || 'jpg'}`,
        { type: file.type }
      )
      setFieldValue(name, 'uploading...')

      const response = await putToS3(fileToUpload, folder, name)

      await checkFileExists(response)

      setFieldValue(name, response)
      previewImage({ file: fileToUpload })
      setSubmitting(false)
      setProgress({
        isLoading: false,
        success: true,
      })
    } catch (error) {
      Sentry.captureException(error)

      setAlertEvent({
        message: t('documents.alerts.error'),
        open: true,
        type: 'error',
      })

      setSubmitting(false)
      setProgress({
        isLoading: false,
        success: false,
      })
    }
  }

  const handleClose = () => {
    setAlertEvent((prevAlertEvent) => ({ ...prevAlertEvent, open: false }))
  }

  const configTextfield: TextFieldProps = {
    ...field,
    ...otherProps,
    fullWidth: true,
    inputProps: {
      accept: '.png, .jpg, .jpeg',
    },
    type: 'file',
    value: field.value || '',
    variant: 'outlined',
  }

  const configInput: InputHTMLAttributes<HTMLInputElement> = {
    ...field,
    accept: 'image/png, image/jpg, image/jpeg',
    hidden: true,
    type: 'file',
  }

  const configGridItem = {
    item: true,
    xs: 12,
  }

  const configCardMedia = {
    sx: {
      backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(${preview})`,
      height: '100%',
      left: 0,
      position: 'absolute',
      right: 0,
      top: 0,
      width: '100%',
    },
  }

  const configIcon = {
    sx: {
      ...(preview
        ? {
            color: '#FFF',
          }
        : {
            color: grey[400],
          }),
    },
  }

  const configTypography = {
    sx: {
      ...(preview
        ? {
            color: '#FFF',
            zIndex: 1,
          }
        : {
            color: grey[800],
          }),
    },
  }

  const configCardContent = {
    sx: {
      alignItems: 'center',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      my: 1,
    },
  }

  if (meta && meta.touched && meta.error) {
    configTextfield.error = true
    configTextfield.helperText = meta.error
  } else {
    configTextfield.error = false
  }

  return (
    <>
      <Grid
        container
        sx={{
          alignContent: 'center',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Grid {...configGridItem}>
          <input
            ref={inputRef}
            {...configInput}
            onChange={async (e) => {
              if (isValid(e.target.files![0])) {
                await handleUpload(e.target.files![0])
              } else {
                setFieldTouched(name, true, false)
              }
            }}
            value=""
          />
          <Card
            sx={{
              height: 220,
              width: 200,
              ...(configTextfield.error ? { borderColor: red[500] } : null),
            }}
            variant="outlined"
          >
            <CardActionArea
              disabled={progress.isLoading}
              onClick={() => inputRef.current?.click()}
            >
              {preview ? (
                <CardMedia component="img" height="140" {...configCardMedia} />
              ) : null}
              <CardContent {...configCardContent}>
                <Avatar
                  sx={{
                    height: 72,
                    mb: 2,
                    width: 72,
                    ...(preview
                      ? {
                          backgroundColor: 'transparent',
                          border: `2px solid ${grey[200]}`,
                        }
                      : {
                          backgroundColor: '#FFF',
                          border: `2px solid ${grey[200]}`,
                        }),
                  }}
                  variant="square"
                >
                  {progress.success ? (
                    <CheckIcon fontSize="large" {...configIcon} />
                  ) : (
                    <CameraFrontIcon fontSize="large" {...configIcon} />
                  )}
                  {progress.isLoading && (
                    <CircularProgress size={48} sx={{ position: 'absolute' }} />
                  )}
                </Avatar>
                <Typography variant="h6" {...configTypography}>
                  {t('images.snap_selfie')}
                </Typography>
                <Typography
                  variant="body2"
                  {...configTypography}
                  align="center"
                >
                  {t('images.or_upload')}
                </Typography>
                <Typography variant="caption" {...configTypography}>
                  {t('images.max_size')}
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
          <ErrorMessage name={name}>
            {(msg) => (
              <Typography sx={{ color: red[600] }} variant="caption">
                {msg}
              </Typography>
            )}
          </ErrorMessage>
        </Grid>
      </Grid>
      <Snackbar
        anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
        autoHideDuration={6000}
        onClose={handleClose}
        open={alertEvent.open}
      >
        <Alert
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              onClick={handleClose}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          }
          elevation={2}
          onClose={handleClose}
          severity={alertEvent.type}
          sx={{
            width: '100%',
          }}
        >
          {alertEvent.message}
        </Alert>
      </Snackbar>
    </>
  )
}

export default ProfileUploadWrapper
